import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  User, Mail, Building, Calendar, Clock, Target,
  BookOpen, Award, BarChart2, Activity, TrendingUp, Layers,
  FileText, ChevronDown, ChevronUp, ChevronRight,
  XCircle, PlayCircle, HelpCircle, CheckCircle,
} from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { useRoleData } from '../../hooks/useRoleData';
import { usePersistentTab } from '../../hooks/usePersistentTab';
import { analyticsAPI } from '../../services/api';
import { formatDate } from '../../utils/dateFormatter';
import Modal from '../../components/common/Modal';
import './TraineeDetails.css';

const TraineeDetails = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const { showNotification } = useAppContext();
  const { filteredUsers = [], filteredModules = [], allModules = [], loading: dataLoading } = useRoleData();

  const [activeTab, setActiveTab] = usePersistentTab('overview');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(null);
  const [showAllEvents, setShowAllEvents] = useState(false);
  const [expandedEvents, setExpandedEvents] = useState({});

  // Module analytics modal state
  const [selectedModuleId, setSelectedModuleId] = useState(null);
  const [moduleAnalyticsData, setModuleAnalyticsData] = useState(null);
  const [moduleAnalyticsLoading, setModuleAnalyticsLoading] = useState(false);
  const [moduleAnalyticsError, setModuleAnalyticsError] = useState(null);

  // Content analytics modal state
  const [selectedContentId, setSelectedContentId] = useState(null);
  const [contentAnalyticsData, setContentAnalyticsData] = useState(null);
  const [contentAnalyticsLoading, setContentAnalyticsLoading] = useState(false);
  const [contentAnalyticsError, setContentAnalyticsError] = useState(null);

  // Find trainee by username from the loaded users
  const trainee = useMemo(() => {
    return filteredUsers.find(u => u.username === username && u.role === 'Trainee');
  }, [filteredUsers, username]);

  // Get enriched module data for this trainee
  const enrolledModuleDetails = useMemo(() => {
    if (!trainee || !trainee.enrolledModules) return [];
    const allModulesList = allModules.length > 0 ? allModules : filteredModules;
    return trainee.enrolledModules
      .map(moduleId => allModulesList.find(m => m.id === moduleId))
      .filter(Boolean);
  }, [trainee, filteredModules, allModules]);

  const completedModuleIds = trainee?.completedModules || [];
  const analyticsProgress = analyticsData?.overallProgress;
  const enrolledModuleCount = analyticsData?.totalModules ?? trainee?.enrolledModules?.length ?? 0;
  const completedModuleCount = analyticsData?.completedModules ?? completedModuleIds.length;
  const overallProgress = analyticsProgress ?? trainee?.progress ?? 0;

  const analyticsModuleProgressMap = useMemo(() => {
    if (!analyticsData?.moduleProgress) return new Map();
    return new Map(analyticsData.moduleProgress.map((module) => [module.moduleId, module]));
  }, [analyticsData]);

  // Fetch analytics once so overview and analytics stay aligned.
  useEffect(() => {
    if (trainee && !analyticsData && !analyticsLoading) {
      const fetchAnalytics = async () => {
        try {
          setAnalyticsLoading(true);
          const response = await analyticsAPI.getTraineeDetailAnalytics(trainee.id);
          if (response.data.success) {
            setAnalyticsData(response.data.data);
          } else {
            setAnalyticsError('Failed to fetch analytics');
          }
        } catch (err) {
          console.error('Error fetching trainee analytics:', err);
          setAnalyticsError(err.response?.data?.error || 'Failed to load analytics');
          showNotification(err.response?.data?.error || 'Failed to load analytics', 'error');
        } finally {
          setAnalyticsLoading(false);
        }
      };
      fetchAnalytics();
    }
  }, [trainee, analyticsData, analyticsLoading, showNotification]);

  const toggleEventExpanded = (eventId) => {
    setExpandedEvents(prev => ({ ...prev, [eventId]: !prev[eventId] }));
  };

  const openModuleAnalytics = useCallback(async (moduleId) => {
    if (!trainee) return;
    setSelectedModuleId(moduleId);
    setModuleAnalyticsData(null);
    setModuleAnalyticsError(null);
    setModuleAnalyticsLoading(true);
    try {
      const response = await analyticsAPI.getTraineeModuleAnalytics(trainee.id, moduleId);
      if (response.data.success) {
        setModuleAnalyticsData(response.data.data);
      } else {
        setModuleAnalyticsError('Failed to fetch module analytics');
      }
    } catch (err) {
      console.error('Error fetching module analytics:', err);
      setModuleAnalyticsError(err.response?.data?.error || 'Failed to load module analytics');
    } finally {
      setModuleAnalyticsLoading(false);
    }
  }, [trainee]);

  const closeModuleAnalytics = useCallback(() => {
    setSelectedModuleId(null);
    setModuleAnalyticsData(null);
    setModuleAnalyticsError(null);
    setModuleAnalyticsLoading(false);
  }, []);

  const openContentAnalytics = useCallback(async (contentId) => {
    if (!trainee || !selectedModuleId) return;
    setSelectedContentId(contentId);
    setContentAnalyticsData(null);
    setContentAnalyticsError(null);
    setContentAnalyticsLoading(true);
    try {
      const response = await analyticsAPI.getTraineeContentAnalytics(trainee.id, selectedModuleId, contentId);
      if (response.data.success) {
        setContentAnalyticsData(response.data.data);
      } else {
        setContentAnalyticsError('Failed to fetch content analytics');
      }
    } catch (err) {
      console.error('Error fetching content analytics:', err);
      setContentAnalyticsError(err.response?.data?.error || 'Failed to load content analytics');
    } finally {
      setContentAnalyticsLoading(false);
    }
  }, [trainee, selectedModuleId]);

  const closeContentAnalytics = useCallback(() => {
    setSelectedContentId(null);
    setContentAnalyticsData(null);
    setContentAnalyticsError(null);
    setContentAnalyticsLoading(false);
  }, []);

  const formatEventName = (name) => {
    return name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (dataLoading) {
    return (
      <div className="trainee-details">
        <div className="page-header">
          <h1>Loading trainee details...</h1>
        </div>
      </div>
    );
  }

  if (!trainee) {
    return (
      <div className="trainee-details">
        <div className="page-header">
          <div className="page-header-left">
            <h1>Trainee Not Found</h1>
            <p>No trainee found with username <strong>@{username}</strong></p>
          </div>
        </div>
      </div>
    );
  }

  const renderOverviewTab = () => (
    <div className="trainee-details__overview">
      {/* Profile Card */}
      <div className="card trainee-details__profile-card">
        <div className="trainee-details__profile-header">
          <div
            className="avatar trainee-details__avatar"
            style={{
              width: 80,
              height: 80,
              fontSize: '1.8rem',
              background: 'linear-gradient(135deg, var(--color-lime), var(--color-medium-green))',
            }}
          >
            {trainee.avatar ? (
              <img src={trainee.avatar} alt={trainee.name} />
            ) : (
              trainee.initials
            )}
          </div>
          <div className="trainee-details__profile-info">
            <h2 className="trainee-details__name">{trainee.name}</h2>
            <span className="trainee-details__username">@{trainee.username}</span>
            <div className="trainee-details__badges">
              <span className="badge badge-green">{trainee.role}</span>
              <span className={`badge ${trainee.status === 'Active' ? 'badge-green' : 'badge-gray'}`}>
                {trainee.status}
              </span>
            </div>
          </div>
        </div>

        <div className="trainee-details__info-grid">
          <div className="trainee-details__info-item">
            <Mail size={14} className="trainee-details__info-icon" />
            <div>
              <span className="trainee-details__info-label">Email</span>
              <span className="trainee-details__info-value">{trainee.email}</span>
            </div>
          </div>
          <div className="trainee-details__info-item">
            <Building size={14} className="trainee-details__info-icon" />
            <div>
              <span className="trainee-details__info-label">Department</span>
              <span className="trainee-details__info-value">{trainee.department}</span>
            </div>
          </div>
          <div className="trainee-details__info-item">
            <Calendar size={14} className="trainee-details__info-icon" />
            <div>
              <span className="trainee-details__info-label">Joined</span>
              <span className="trainee-details__info-value">{formatDate(trainee.joinDate)}</span>
            </div>
          </div>
          <div className="trainee-details__info-item">
            <Clock size={14} className="trainee-details__info-icon" />
            <div>
              <span className="trainee-details__info-label">Last Active</span>
              <span className="trainee-details__info-value">{formatDate(trainee.lastActive)}</span>
            </div>
          </div>
          <div className="trainee-details__info-item">
            <Target size={14} className="trainee-details__info-icon" />
            <div>
              <span className="trainee-details__info-label">Access Token</span>
              <span className="trainee-details__info-value trainee-details__token">{trainee.token}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Summary */}
      <div className="trainee-details__stats">
        {[
          { icon: BookOpen, label: 'Enrolled Modules', value: enrolledModuleCount, color: 'blue' },
          { icon: Award, label: 'Completed', value: completedModuleCount, color: 'green' },
          { icon: TrendingUp, label: 'Overall Progress', value: `${overallProgress}%`, color: 'yellow' },
        ].map(({ icon: Icon, label, value, color }) => (
          <div key={label} className={`trainee-details__stat-card trainee-details__stat-card--${color}`}>
            <div className="trainee-details__stat-icon">
              <Icon size={20} />
            </div>
            <div>
              <span className="trainee-details__stat-value">{value}</span>
              <span className="trainee-details__stat-label">{label}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Progress Bar */}
      <div className="card">
        <div className="card-header">
          <h3><BarChart2 size={16} style={{ marginRight: '6px' }} /> Overall Progress</h3>
          <span className="trainee-details__progress-pct">{overallProgress}%</span>
        </div>
        <div className="card-body">
          <div className="progress-bar" style={{ height: 10 }}>
            <div className="progress-bar-fill" style={{ width: `${overallProgress}%` }} />
          </div>
          <p className="trainee-details__progress-summary">
            Completed {completedModuleCount} of {enrolledModuleCount} enrolled modules
          </p>
        </div>
      </div>

      {/* Enrolled Modules */}
      <div className="card">
        <div className="card-header">
          <h3><BookOpen size={16} style={{ marginRight: '6px' }} /> Enrolled Modules</h3>
          <span className="badge badge-dark">{enrolledModuleDetails.length} modules</span>
        </div>
        {enrolledModuleDetails.length > 0 ? (
          <div className="trainee-details__modules-list">
            {enrolledModuleDetails.map((module) => {
              const moduleAnalytics = analyticsModuleProgressMap.get(module.id);
              const isCompleted = moduleAnalytics ? moduleAnalytics.completed : completedModuleIds.includes(module.id);
              return (
              <div
                  key={module.id}
                  className="trainee-details__module-row trainee-details__module-row--clickable"
                  onClick={() => openModuleAnalytics(module.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openModuleAnalytics(module.id); } }}
                >
                  <div className="trainee-details__module-icon">
                    <BookOpen size={16} />
                  </div>
                  <div className="trainee-details__module-info">
                    <span className="trainee-details__module-title">{module.title}</span>
                    <span className="trainee-details__module-meta">
                      {module.category} &bull; {module.level} &bull; {module.duration} min
                    </span>
                  </div>
                  <span className={`badge ${isCompleted ? 'badge-green' : 'badge-gray'}`}>
                    {isCompleted ? 'Completed' : 'In Progress'}
                  </span>
                  <ChevronRight size={16} className="trainee-details__module-chevron" />
                </div>
              );
            })}
          </div>
        ) : (
          <div className="card-body" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
            <BookOpen size={32} style={{ marginBottom: '0.5rem', opacity: 0.4 }} />
            <p>No modules enrolled yet.</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderAnalyticsTab = () => {
    if (analyticsLoading) {
      return (
        <div className="trainee-details__analytics-loading">
          <Activity size={32} style={{ opacity: 0.4 }} />
          <p>Loading analytics data...</p>
        </div>
      );
    }

    if (analyticsError || !analyticsData) {
      return (
        <div className="trainee-details__analytics-loading">
          <XCircle size={32} style={{ opacity: 0.4 }} />
          <p>{analyticsError || 'No analytics data available'}</p>
        </div>
      );
    }

    const {
      overallProgress, totalModules, completedModules: completedCount,
      totalTimeSpentHours, totalContentEnrolled, completedContent,
      moduleProgress, recentEvents
    } = analyticsData;

    const visibleEvents = showAllEvents ? recentEvents : recentEvents.slice(0, 15);

    return (
      <div className="trainee-details__analytics">
        {/* Analytics Stats */}
        <div className="analytics__stats">
          {[
            { icon: BookOpen, label: 'Enrolled Modules', value: totalModules, color: 'blue' },
            { icon: Award, label: 'Completed Modules', value: completedCount, color: 'green' },
            { icon: TrendingUp, label: 'Overall Progress', value: `${overallProgress}%`, color: 'yellow' },
            { icon: Clock, label: 'Time Spent', value: `${totalTimeSpentHours}h`, color: 'dark' },
            { icon: Layers, label: 'Content Enrolled', value: totalContentEnrolled, color: 'blue' },
            { icon: FileText, label: 'Content Completed', value: completedContent, color: 'green' },
          ].map(({ icon: Icon, label, value, color }) => (
            <div key={label} className={`analytics__stat-card analytics__stat-card--${color}`}>
              <div className="analytics__stat-icon">
                <Icon size={20} />
              </div>
              <div>
                <span className="analytics__stat-value">{value}</span>
                <span className="analytics__stat-label">{label}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Module Progress Table */}
        <div className="card">
          <div className="card-header">
            <h3><BookOpen size={16} style={{ marginRight: '6px' }} /> Module Progress</h3>
            <span className="badge badge-dark">{moduleProgress.length} modules</span>
          </div>
          {moduleProgress.length > 0 ? (
            <div className="analytics__table">
              <div className="analytics__table-header">
                <span>Module</span>
                <span>Category</span>
                <span>Duration</span>
                <span>Progress</span>
                <span>Time Spent</span>
                <span>Status</span>
              </div>
              {moduleProgress.map((module) => (
                <div
                  key={module.moduleId}
                  className="analytics__table-row analytics__table-row--clickable"
                  onClick={() => openModuleAnalytics(module.moduleId)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openModuleAnalytics(module.moduleId); } }}
                >
                  <div className="analytics__table-module">
                    <div className="analytics__table-icon">
                      <BookOpen size={14} />
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <span style={{ display: 'block' }}>{module.title}</span>
                      {module.lastAccessed && (
                        <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
                          Last accessed: {new Date(module.lastAccessed).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="badge badge-dark">{module.category}</span>
                  <span className="analytics__table-cell">{module.duration} min</span>
                  <div className="analytics__table-progress">
                    <div className="progress-bar" style={{ height: 6, flex: 1 }}>
                      <div className="progress-bar-fill" style={{ width: `${module.progress}%` }} />
                    </div>
                    <span
                      className={`analytics__table-pct ${
                        module.progress >= 75 ? 'analytics__table-pct--high' :
                        module.progress >= 50 ? 'analytics__table-pct--mid' : 'analytics__table-pct--low'
                      }`}
                    >
                      {module.progress}%
                    </span>
                  </div>
                  <span className="analytics__table-cell">{module.timeSpentHours}h</span>
                  <span className={`badge ${module.completed ? 'badge-green' : 'badge-gray'}`}>
                    {module.completed ? 'Completed' : 'In Progress'}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="card-body" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
              <BookOpen size={32} style={{ marginBottom: '0.5rem', opacity: 0.4 }} />
              <p>No module progress data yet.</p>
            </div>
          )}
        </div>

        {/* Event Log */}
        <div className="card">
          <div className="card-header">
            <h3><Activity size={16} style={{ marginRight: '6px' }} /> Activity Log</h3>
            <span className="badge badge-dark">{recentEvents.length} events</span>
          </div>
          {recentEvents.length > 0 ? (
            <>
              <div className="analytics__table">
                <div className="analytics__table-header analytics__table-header--events">
                  <span>Event</span>
                  <span>Time</span>
                  <span>Module</span>
                  <span>Content</span>
                  <span>Scene</span>
                  <span>Details</span>
                </div>
                {visibleEvents.map((event) => (
                  <React.Fragment key={event.id}>
                    <div className="analytics__table-row analytics__table-row--events">
                      <span className="analytics__table-cell">
                        <span className="analytics__event-badge">
                          {formatEventName(event.eventName)}
                        </span>
                      </span>
                      <span className="analytics__table-cell" style={{ fontSize: '0.75rem' }}>
                        {new Date(event.eventTime).toLocaleString()}
                      </span>
                      <span className="analytics__table-cell">
                        {event.moduleId ? `Module ${event.moduleId}` : '—'}
                      </span>
                      <span className="analytics__table-cell">
                        {event.contentId ? `Content ${event.contentId}` : '—'}
                      </span>
                      <span className="analytics__table-cell">
                        {event.sceneName || '—'}
                      </span>
                      <span className="analytics__table-cell">
                        {event.data && Object.keys(event.data).length > 0 ? (
                          <button
                            className="btn btn-outline"
                            style={{ padding: '2px 8px', fontSize: '0.7rem' }}
                            onClick={() => toggleEventExpanded(event.id)}
                          >
                            {expandedEvents[event.id] ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                            {expandedEvents[event.id] ? ' Hide' : ' View'}
                          </button>
                        ) : '—'}
                      </span>
                    </div>
                    {expandedEvents[event.id] && event.data && (
                      <div className="analytics__event-data">
                        <pre>{JSON.stringify(event.data, null, 2)}</pre>
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
              {recentEvents.length > 15 && (
                <div style={{ padding: 'var(--space-3) var(--space-5)', textAlign: 'center' }}>
                  <button
                    className="btn btn-outline"
                    onClick={() => setShowAllEvents(!showAllEvents)}
                  >
                    {showAllEvents ? 'Show Less' : `Show All ${recentEvents.length} Events`}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="card-body" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
              <Activity size={32} style={{ marginBottom: '0.5rem', opacity: 0.4 }} />
              <p>No activity events recorded yet.</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="trainee-details">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <h1>
            <User size={20} style={{ marginRight: '8px' }} />
            {trainee.name}
          </h1>
          <p className="trainee-details__subtitle">
            @{trainee.username} &bull; {trainee.department} &bull; {trainee.email}
            {trainee.status !== 'Active' && (
              <span className="badge badge-gray" style={{ marginLeft: '8px' }}>{trainee.status}</span>
            )}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="trainee-details__tabs">
        <button
          className={`trainee-details__tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <User size={15} />
          Overview
        </button>
        <button
          className={`trainee-details__tab ${activeTab === 'analytics' ? 'active' : ''}`}
          onClick={() => setActiveTab('analytics')}
        >
          <Activity size={15} />
          Analytics
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && renderOverviewTab()}
      {activeTab === 'analytics' && renderAnalyticsTab()}

      {/* Module Analytics Modal */}
      <Modal
        isOpen={selectedModuleId !== null}
        onClose={closeModuleAnalytics}
        title={moduleAnalyticsData ? moduleAnalyticsData.module.title : 'Module Analytics'}
        size="lg"
      >
        {moduleAnalyticsLoading && (
          <div className="module-analytics-modal__loading">
            <Activity size={32} style={{ opacity: 0.4 }} />
            <p>Loading module analytics...</p>
          </div>
        )}
        {moduleAnalyticsError && (
          <div className="module-analytics-modal__loading">
            <XCircle size={32} style={{ opacity: 0.4 }} />
            <p>{moduleAnalyticsError}</p>
          </div>
        )}
        {moduleAnalyticsData && (
          <>
            {/* Header Stats */}
            <div className="module-analytics-modal__header-stats">
              <div className="module-analytics-modal__stat">
                <span className="module-analytics-modal__stat-label">Category</span>
                <span>{moduleAnalyticsData.module.category}</span>
              </div>
              <div className="module-analytics-modal__stat">
                <span className="module-analytics-modal__stat-label">Level</span>
                <span>{moduleAnalyticsData.module.level || '—'}</span>
              </div>
              <div className="module-analytics-modal__stat">
                <span className="module-analytics-modal__stat-label">Duration</span>
                <span>{moduleAnalyticsData.module.durationMinutes} min</span>
              </div>
              <div className="module-analytics-modal__stat">
                <span className="module-analytics-modal__stat-label">Content</span>
                <span>{moduleAnalyticsData.contentStats.totalContent} items</span>
              </div>
            </div>

            {/* Progress Stats */}
            <div className="module-analytics-modal__progress-stats">
              <div className="module-analytics-modal__progress-card">
                <span className="module-analytics-modal__progress-value">{moduleAnalyticsData.enrollment.progress}%</span>
                <span className="module-analytics-modal__progress-label">Module Progress</span>
                <div className="progress-bar" style={{ height: 6, width: '100%' }}>
                  <div className="progress-bar-fill" style={{ width: `${moduleAnalyticsData.enrollment.progress}%` }} />
                </div>
              </div>
              <div className="module-analytics-modal__progress-card">
                <span className="module-analytics-modal__progress-value">{moduleAnalyticsData.enrollment.timeSpentHours}h</span>
                <span className="module-analytics-modal__progress-label">Time Spent</span>
              </div>
              <div className="module-analytics-modal__progress-card">
                <span className={`module-analytics-modal__progress-value ${moduleAnalyticsData.enrollment.completed ? 'module-analytics-modal__progress-value--completed' : ''}`}>
                  {moduleAnalyticsData.enrollment.completed ? 'Completed' : 'In Progress'}
                </span>
                <span className="module-analytics-modal__progress-label">Status</span>
              </div>
              <div className="module-analytics-modal__progress-card">
                <span className="module-analytics-modal__progress-value">
                  {moduleAnalyticsData.enrollment.lastAccessed
                    ? new Date(moduleAnalyticsData.enrollment.lastAccessed).toLocaleDateString()
                    : '—'}
                </span>
                <span className="module-analytics-modal__progress-label">Last Accessed</span>
              </div>
            </div>

            {/* Content Progress Table */}
            <div className="card">
              <div className="card-header">
                <h3><FileText size={16} style={{ marginRight: '6px' }} /> Content Progress</h3>
                <span className="badge badge-dark">
                  {moduleAnalyticsData.contentStats.completedContent} / {moduleAnalyticsData.contentStats.totalContent} completed
                </span>
              </div>
              {moduleAnalyticsData.contentProgress.length > 0 ? (
                <div className="analytics__table">
                  <div className="analytics__table-header module-analytics-modal__content-header">
                    <span>Content</span>
                    <span>Duration</span>
                    <span>Time Spent</span>
                    <span>Progress</span>
                    <span>Status</span>
                    <span></span>
                  </div>
                  {moduleAnalyticsData.contentProgress.map((content) => {
                    const progressPct = content.completed ? 100 : (
                      content.durationMinutes > 0
                        ? Math.min(100, Math.round((content.timeSpentSeconds / 60 / content.durationMinutes) * 100))
                        : 0
                    );
                    return (
                      <div
                        key={content.contentId}
                        className="analytics__table-row module-analytics-modal__content-row module-analytics-modal__content-row--clickable"
                        onClick={() => openContentAnalytics(content.contentId)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openContentAnalytics(content.contentId); } }}
                      >
                        <div className="analytics__table-module">
                          <div className="analytics__table-icon">
                            {content.contentType === 'video' ? <PlayCircle size={14} /> : <FileText size={14} />}
                          </div>
                          <span>{content.title}</span>
                        </div>
                        <span className="analytics__table-cell">{content.durationMinutes} min</span>
                        <span className="analytics__table-cell">
                          {content.timeSpentSeconds >= 60
                            ? `${Math.round(content.timeSpentSeconds / 60)} min`
                            : `${content.timeSpentSeconds}s`}
                        </span>
                        <div className="analytics__table-progress">
                          <div className="progress-bar" style={{ height: 6, flex: 1 }}>
                            <div className="progress-bar-fill" style={{ width: `${progressPct}%` }} />
                          </div>
                          <span
                            className={`analytics__table-pct ${
                              progressPct >= 75 ? 'analytics__table-pct--high' :
                              progressPct >= 50 ? 'analytics__table-pct--mid' : 'analytics__table-pct--low'
                            }`}
                          >
                            {progressPct}%
                          </span>
                        </div>
                        <span className={`badge ${content.completed ? 'badge-green' : 'badge-gray'}`}>
                          {content.completed ? 'Completed' : 'Pending'}
                        </span>
                        <ChevronRight size={14} className="analytics__table-chevron" />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="card-body" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                  <HelpCircle size={32} style={{ marginBottom: '0.5rem', opacity: 0.4 }} />
                  <p>No content items in this module.</p>
                </div>
              )}
            </div>

            {/* Recent Activity */}
            {moduleAnalyticsData.recentEvents.length > 0 && (
              <div className="card">
                <div className="card-header">
                  <h3><Activity size={16} style={{ marginRight: '6px' }} /> Recent Activity</h3>
                  <span className="badge badge-dark">{moduleAnalyticsData.recentEvents.length} events</span>
                </div>
                <div className="analytics__table">
                  <div className="analytics__table-header analytics__table-header--events">
                    <span>Event</span>
                    <span>Time</span>
                    <span>Content</span>
                    <span>Scene</span>
                  </div>
                  {moduleAnalyticsData.recentEvents.map((event) => (
                    <div key={event.id} className="analytics__table-row analytics__table-row--events">
                      <span className="analytics__table-cell">
                        <span className="analytics__event-badge">
                          {formatEventName(event.eventName)}
                        </span>
                      </span>
                      <span className="analytics__table-cell" style={{ fontSize: '0.75rem' }}>
                        {new Date(event.eventTime).toLocaleString()}
                      </span>
                      <span className="analytics__table-cell">
                        {event.contentId
                          ? moduleAnalyticsData.contentProgress.find(c => c.contentId === event.contentId)?.title || `Content ${event.contentId}`
                          : '—'}
                      </span>
                      <span className="analytics__table-cell">
                        {event.sceneName || '—'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </Modal>

      {/* Content Analytics Modal */}
      <Modal
        isOpen={selectedContentId !== null}
        onClose={closeContentAnalytics}
        title={contentAnalyticsData ? contentAnalyticsData.content.title : 'Content Analytics'}
        size="md"
      >
        {contentAnalyticsLoading && (
          <div className="content-analytics-modal__loading">
            <Activity size={32} style={{ opacity: 0.4 }} />
            <p>Loading content analytics...</p>
          </div>
        )}
        {contentAnalyticsError && (
          <div className="content-analytics-modal__loading">
            <XCircle size={32} style={{ opacity: 0.4 }} />
            <p>{contentAnalyticsError}</p>
          </div>
        )}
        {contentAnalyticsData && (
          <>
            {/* Header Stats */}
            <div className="content-analytics-modal__header-stats">
              <div className="content-analytics-modal__stat">
                <span className="content-analytics-modal__stat-label">Category</span>
                <span>{contentAnalyticsData.content.category}</span>
              </div>
              <div className="content-analytics-modal__stat">
                <span className="content-analytics-modal__stat-label">Type</span>
                <span>{contentAnalyticsData.content.contentType === 'vr_app' ? 'VR App' : 'Web Tour'}</span>
              </div>
              <div className="content-analytics-modal__stat">
                <span className="content-analytics-modal__stat-label">Platform</span>
                <span>{contentAnalyticsData.content.platform}</span>
              </div>
              <div className="content-analytics-modal__stat">
                <span className="content-analytics-modal__stat-label">Duration</span>
                <span>{contentAnalyticsData.content.durationMinutes} min</span>
              </div>
            </div>

            {/* Progress & Stats */}
            <div className="content-analytics-modal__progress-stats">
              <div className="content-analytics-modal__progress-card">
                <span className="content-analytics-modal__progress-value">{contentAnalyticsData.stats.progress}%</span>
                <span className="content-analytics-modal__progress-label">Progress</span>
                <div className="progress-bar" style={{ height: 6, width: '100%' }}>
                  <div className="progress-bar-fill" style={{ width: `${contentAnalyticsData.stats.progress}%` }} />
                </div>
              </div>
              <div className="content-analytics-modal__progress-card">
                <span className="content-analytics-modal__progress-value">
                  {contentAnalyticsData.stats.totalTimeSpentMinutes} min
                </span>
                <span className="content-analytics-modal__progress-label">Time Spent</span>
              </div>
              <div className="content-analytics-modal__progress-card">
                <span className="content-analytics-modal__progress-value">{contentAnalyticsData.stats.sessionCount}</span>
                <span className="content-analytics-modal__progress-label">Sessions</span>
              </div>
              <div className="content-analytics-modal__progress-card">
                <span className="content-analytics-modal__progress-value">{contentAnalyticsData.stats.launchCount}</span>
                <span className="content-analytics-modal__progress-label">Launches</span>
              </div>
              <div className="content-analytics-modal__progress-card">
                <span className={`content-analytics-modal__progress-value ${contentAnalyticsData.stats.completed ? 'content-analytics-modal__progress-value--completed' : ''}`}>
                  {contentAnalyticsData.stats.completed ? 'Completed' : 'In Progress'}
                </span>
                <span className="content-analytics-modal__progress-label">Status</span>
              </div>
              <div className="content-analytics-modal__progress-card">
                <span className="content-analytics-modal__progress-value">
                  {contentAnalyticsData.stats.lastAccess
                    ? new Date(contentAnalyticsData.stats.lastAccess).toLocaleDateString()
                    : '—'}
                </span>
                <span className="content-analytics-modal__progress-label">Last Accessed</span>
              </div>
            </div>

            {/* Assessment Results */}
            {contentAnalyticsData.assessmentSummary && (
              <div className="card">
                <div className="card-header">
                  <h3><Award size={16} style={{ marginRight: '6px' }} /> Assessment Results</h3>
                  {contentAnalyticsData.assessmentSummary.passed ? (
                    <span className="badge badge-green">Passed</span>
                  ) : (
                    <span className="badge" style={{ background: '#dc2626', color: '#fff' }}>Not Passed</span>
                  )}
                </div>
                <div className="content-analytics-modal__progress-stats">
                  <div className="content-analytics-modal__progress-card">
                    <span className="content-analytics-modal__progress-value">
                      {contentAnalyticsData.assessmentSummary.latestPercent}%
                    </span>
                    <span className="content-analytics-modal__progress-label">Latest Score</span>
                  </div>
                  <div className="content-analytics-modal__progress-card">
                    <span className="content-analytics-modal__progress-value">
                      {contentAnalyticsData.assessmentSummary.latestScore}/{contentAnalyticsData.assessmentSummary.latestTotal}
                    </span>
                    <span className="content-analytics-modal__progress-label">Correct</span>
                  </div>
                  <div className="content-analytics-modal__progress-card">
                    <span className="content-analytics-modal__progress-value">
                      {contentAnalyticsData.assessmentSummary.bestPercent}%
                    </span>
                    <span className="content-analytics-modal__progress-label">Best Score</span>
                  </div>
                  <div className="content-analytics-modal__progress-card">
                    <span className="content-analytics-modal__progress-value">
                      {contentAnalyticsData.assessmentSummary.attempts}
                    </span>
                    <span className="content-analytics-modal__progress-label">Attempts</span>
                  </div>
                </div>
                {contentAnalyticsData.assessments.length > 0 && (
                  <div className="analytics__table">
                    <div className="analytics__table-header analytics__table-header--events">
                      <span>Attempt</span>
                      <span>Score</span>
                      <span>Result</span>
                      <span>Date</span>
                    </div>
                    {contentAnalyticsData.assessments.map((a) => (
                      <div key={a.id} className="analytics__table-row analytics__table-row--events">
                        <span className="analytics__table-cell">#{a.attempt}</span>
                        <span className="analytics__table-cell">
                          {a.score}/{a.totalQuestions} ({a.scorePercent}%)
                        </span>
                        <span className="analytics__table-cell">
                          {a.passed ? (
                            <span className="badge badge-green" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <CheckCircle size={12} /> Pass
                            </span>
                          ) : (
                            <span className="badge" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#dc2626', color: '#fff' }}>
                              <XCircle size={12} /> Fail
                            </span>
                          )}
                        </span>
                        <span className="analytics__table-cell" style={{ fontSize: '0.75rem' }}>
                          {new Date(a.completedAt).toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Recent Events */}
            {contentAnalyticsData.recentEvents.length > 0 && (
              <div className="card">
                <div className="card-header">
                  <h3><Activity size={16} style={{ marginRight: '6px' }} /> Event Timeline</h3>
                  <span className="badge badge-dark">{contentAnalyticsData.recentEvents.length} events</span>
                </div>
                <div className="analytics__table">
                  <div className="analytics__table-header analytics__table-header--events">
                    <span>Event</span>
                    <span>Time</span>
                    <span>Scene</span>
                    <span>Details</span>
                  </div>
                  {contentAnalyticsData.recentEvents.map((event) => (
                    <React.Fragment key={event.id}>
                      <div className="analytics__table-row analytics__table-row--events">
                        <span className="analytics__table-cell">
                          <span className="analytics__event-badge">
                            {formatEventName(event.eventName)}
                          </span>
                        </span>
                        <span className="analytics__table-cell" style={{ fontSize: '0.75rem' }}>
                          {new Date(event.eventTime).toLocaleString()}
                        </span>
                        <span className="analytics__table-cell">
                          {event.sceneName || '—'}
                        </span>
                        <span className="analytics__table-cell">
                          {event.data && Object.keys(event.data).length > 0 ? (
                            <button
                              className="btn btn-outline"
                              style={{ padding: '2px 8px', fontSize: '0.7rem' }}
                              onClick={() => toggleEventExpanded(event.id)}
                            >
                              {expandedEvents[event.id] ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                              {expandedEvents[event.id] ? ' Hide' : ' View'}
                            </button>
                          ) : '—'}
                        </span>
                      </div>
                      {expandedEvents[event.id] && event.data && (
                        <div className="analytics__event-data">
                          <pre>{JSON.stringify(event.data, null, 2)}</pre>
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </Modal>
    </div>
  );
};

export default TraineeDetails;
